import { useEffect } from "react";

import { readState } from "@/__main__/app-state.mjs";
import { IS_APP, IS_MAC, IS_NODE } from "@/__main__/constants.mjs";
import type { FeatureFlags } from "@/__main__/feature-flags.mjs";
import { allowRead } from "@/__main__/feature-flags.mjs";
import blitzMessage, { EVENTS } from "@/__main__/ipc-core.mjs";
import { setVolatileKV } from "@/app/actions.mjs";
import clone from "@/util/clone.mjs";
import { devDebug, devError, devWarn } from "@/util/dev.mjs";
import { useSnapshot } from "@/util/use-snapshot.mjs";
import CRC, { str2ab8 } from "@/vendor/crc32.mjs";

const env = {
  id: null,
  appVersion: null,
};

export async function getEnvironmentIdentifier() {
  if (IS_NODE || env.id) return env;

  if (IS_APP) {
    env.id = await blitzMessage(EVENTS.MACHINE_ID);
    env.appVersion = await blitzMessage(EVENTS.APP_VERSION);
  } else {
    const { default: getBrowserFingerprint } = await import(
      "get-browser-fingerprint"
    );
    env.id = await getBrowserFingerprint();
    env.appVersion = "web";
  }

  if (!env.id && !IS_MAC) devError("Unable to get machine ID");
  return env;
}
// Return the feature's group: a is control group, b is experimental group
// CMS is a configuration that controls traffic between two groups. But the user's specific experimental group is determined by the front-end based on the user's machine ID.
// check the code detail: src/app/actions.mts -> overrideFeatureFlagsFromCMS()
// Therefore, only features configured in CMS have versions A and B.
// If the feature of the current user is activated, then the user is considered to have entered version B (true), otherwise it is version A(false).

export function getABFeatureValue(featureName: keyof FeatureFlags): boolean;
export function getABFeatureValue(): {
  [key in keyof FeatureFlags]: boolean;
};
export function getABFeatureValue(
  featureName?: keyof FeatureFlags,
): boolean | { [key in keyof FeatureFlags]: boolean } {
  allowRead();
  const cmsFeatureFlags = readState.cms.featureFlags;
  const features = readState.features;

  if (featureName) {
    return cmsFeatureFlags.hasOwnProperty(featureName) && features[featureName];
  }
  const abFeatures = {} as { [key in keyof FeatureFlags]: boolean };
  for (const cmsFeature of cmsFeatureFlags) {
    if (!(cmsFeature && cmsFeature.feature in features)) {
      continue;
    }
    const name = cmsFeature.feature;
    abFeatures[name] = !!features[name];
  }
  return abFeatures;
}

export function getFeatureIdentifier(id, salt = "") {
  return new CRC().update(str2ab8(id + salt)).digest("raw");
}

export async function initABFeature(
  [namespace, featureName]: [string, string],
  enabledPercentage: number,
) {
  if (!Number.isInteger(enabledPercentage))
    devError("Percentage must be an integer [1, 100]");

  if (enabledPercentage === 100)
    devError("Careful with 100% A/B feature rollouts, they do not include SSR");

  const subFeatures = clone(readState.volatile.subFeatures) ?? {};
  const features = subFeatures[namespace] || {};

  if (IS_NODE || features[featureName] !== undefined)
    return features[featureName] ?? false;

  const { id } = await getEnvironmentIdentifier();
  const featureIdent = getFeatureIdentifier(id, featureName);
  const enabled = featureIdent % 100 < enabledPercentage;
  devDebug(`Subfeature ${namespace}-${featureName}`, {
    enabled,
    featureIdent,
    enabledPercentage,
    comparison: featureIdent % 100,
  });

  subFeatures[namespace] = features;
  features[featureName] = enabled;
  setVolatileKV("subFeatures", subFeatures);
  return enabled;
}

export function useABFeature([namespace, featureName]: [string, string]) {
  const {
    volatile: { subFeatures },
  } = useSnapshot(readState);

  const featureEnabled = subFeatures?.[namespace]?.[featureName];
  useEffect(() => {
    if (featureEnabled === null || featureEnabled === undefined)
      devWarn(`SubFeature ${namespace}-${featureName} was not initialized`);
  }, [namespace, featureName]);

  return featureEnabled;
}

declare module "@/__main__/app-state.mjs" {
  interface VolatileState {
    subFeatures?: { [key: string]: { [key: string]: boolean } };
  }
}
